import { useState, useEffect, useRef } from 'react';
import { useMapElement } from '../contextProviders/MapElementProvider';
import NameInput from '../common/NameInput';
import ModalBody from '../../../generic/modal/ModalBody';
import ModalFooter from '../../../generic/modal/ModalFooter';
import { isNullOrWhitespace } from '../../../../utils/guard';
import type { MapElementRequestAPI } from '../../../../sources/graph';
import FormGroup from '../../../generic/FormGroup';
import Footer from '../common/Footer';
import translations from '../../../../translations';
import type { MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { getSharedStyles } from '../../../../utils/select';
import OutcomeList from '../common/outcomes/OutcomeList';
import { initializeApprovalOutcomeIds } from '../../../../utils/approval';

const ApprovalConfiguration = () => {
    const { mapElement, onSaveMapElement, onClose, onUpdateName, setMapElement } = useMapElement();
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const isFormValid = isNullOrWhitespace(mapElement?.developerName) === false;
    const [approverEmails, setApproverEmails] = useState<
        MultiValue<{ value: string; label: string }>
    >([]);
    const [approverRoles, setApproverRoles] = useState<
        MultiValue<{ value: string; label: string }>
    >([]);
    const [minimumApprovals, setMinimumApprovals] = useState(1);
    const [minimumDenials, setMinimumDenials] = useState(1);
    const isInitialized = useRef(false);

    useEffect(() => {
        if (!isInitialized.current && mapElement) {
            // Initialize approval outcome IDs if they're not set
            const initializedMapElement = initializeApprovalOutcomeIds(mapElement);
            if (initializedMapElement !== mapElement) {
                setMapElement(initializedMapElement);
            }

            if (mapElement.approval?.approvalDetails?.[0]) {
                const approvalDetail = mapElement.approval.approvalDetails[0];

                if (approvalDetail.emails) {
                    setApproverEmails(
                        approvalDetail.emails.map((email) => ({ value: email, label: email })),
                    );
                }
                if (approvalDetail.roles) {
                    setApproverRoles(
                        approvalDetail.roles.map((role) => ({ value: role, label: role })),
                    );
                }
                setMinimumApprovals(approvalDetail.minimumApprovalsRequired ?? 1);
                setMinimumDenials(approvalDetail.minimumDenialsRequired ?? 1);
            }
            isInitialized.current = true;
        }
    }, [mapElement, setMapElement]);

    const onSave = () => {
        setHasSubmitted(true);

        if (isFormValid) {
            const approvalDetailData = {
                emails: approverEmails.map((email) => email.value),
                roles: approverRoles.map((role) => role.value),
                minimumApprovalsRequired: minimumApprovals,
                minimumDenialsRequired: minimumDenials,
            };

            const approvalData = {
                approvalDetails: [approvalDetailData],
                approvalComparisonType: 'All' as const,
                denialComparisonType: 'All' as const,
                approvedOutcomeId: mapElement?.approval?.approvedOutcomeId ?? null,
                deniedOutcomeId: mapElement?.approval?.deniedOutcomeId ?? null,
            };

            const updatedMapElement = {
                ...mapElement,
            };
            updatedMapElement.approval = approvalData;

            onSaveMapElement(updatedMapElement as MapElementRequestAPI);
        }
    };

    if (!mapElement) {
        onClose();
        return;
    }

    const renderBody = () => (
        <>
            <NameInput
                isValid={isFormValid}
                showValidation={hasSubmitted}
                id="approval-name"
                name={mapElement.developerName ?? ''}
                onUpdateName={onUpdateName}
            />

            <FormGroup label="The content of the approval">
                <textarea
                    className="form-control form-textarea"
                    value={mapElement.userContent ?? ''}
                    onChange={(e) =>
                        setMapElement({
                            ...mapElement,
                            userContent: e.target.value,
                        })
                    }
                    rows={10}
                />
            </FormGroup>

            <div className="form-group">
                {/* Approver Emails */}
                <FormGroup label="Approver Email Addresses" htmlFor="approver-emails">
                    <CreatableSelect
                        inputId="approver-emails"
                        isMulti={true}
                        isClearable={true}
                        isSearchable={true}
                        className="form-control-long"
                        styles={getSharedStyles<{ value: string; label: string }, true>()}
                        value={approverEmails}
                        onChange={(newValue) => setApproverEmails(newValue)}
                        placeholder="user1@example.com, user2@example.com"
                        formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                    />
                </FormGroup>

                {/* Approver Roles */}
                <FormGroup label="Approver Roles" htmlFor="approver-roles">
                    <CreatableSelect
                        inputId="approver-roles"
                        isMulti={true}
                        isClearable={true}
                        isSearchable={true}
                        className="form-control-long"
                        styles={getSharedStyles<{ value: string; label: string }, true>()}
                        value={approverRoles}
                        onChange={(newValue) => setApproverRoles(newValue)}
                        placeholder="Manager, Director, Admin"
                        formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                        isValidNewOption={(inputValue) => {
                            return inputValue.trim().length > 0;
                        }}
                    />
                </FormGroup>

                {/* Minimum Approvals */}
                <FormGroup label="Minimum Approvals Required" htmlFor="min-approvals" isRequired>
                    <input
                        type="number"
                        id="min-approvals"
                        name="minimumApprovals"
                        value={minimumApprovals}
                        onChange={(e) => setMinimumApprovals(Number(e.target.value))}
                        required
                        min="0"
                        title="Minimum number of approvals needed to approve this task"
                        className="form-control form-control-width"
                    />
                </FormGroup>

                {/* Minimum Denials */}
                <FormGroup label="Minimum Denials Required" htmlFor="min-denials" isRequired>
                    <input
                        type="number"
                        id="min-denials"
                        name="minimumDenials"
                        value={minimumDenials}
                        onChange={(e) => setMinimumDenials(Number(e.target.value))}
                        required
                        min="0"
                        title="Minimum number of denials needed to deny this task"
                        className="form-control form-control-width"
                    />
                </FormGroup>
            </div>
            <FormGroup
                key="approval-comments"
                label={translations.APPROVAL_comments_label}
                htmlFor="approval-comments"
            >
                <textarea
                    id="approval-comments"
                    data-testid="approval-comments"
                    className="form-control form-textarea"
                    value={mapElement.developerSummary ?? ''}
                    onChange={({ target: { value } }) => {
                        setMapElement({
                            ...mapElement,
                            developerSummary: value,
                        });
                    }}
                />
            </FormGroup>
            <hr className="margin-top margin-bottom" />
            <OutcomeList
                maxOutcomes={2}
                maxOutcomesMessage="Approval elements can only have two outcomes: Approve and Deny."
            />
        </>
    );

    const renderFooter = () => (
        <>
            <Footer save={onSave} cancel={onClose} />
        </>
    );

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </>
    );
};

export default ApprovalConfiguration;
